// Business costs / overheads (GID-Session-015, backlog #681) — shared by the
// Business costs page, the job view and the costs panel. Same maths as the server.

export const EXPENSE_CATEGORIES = [
  { value: 'consumables', label: 'Consumables', hint: 'Hose fittings, nozzles, cleaning agents bought in bulk' },
  { value: 'tools', label: 'Tools & small gear', hint: 'A broom, a ladder — things too small to put on the equipment register' },
  { value: 'repairs', label: 'Repairs & servicing', hint: 'Parts and servicing — tie it to the piece of equipment' },
  { value: 'insurance', label: 'Insurance', hint: '' },
  { value: 'vehicle', label: 'Vehicle', hint: 'Rego, servicing, tyres — fuel for a job goes on the job' },
  { value: 'phone', label: 'Phone & internet', hint: '' },
  { value: 'software', label: 'Software & subscriptions', hint: '' },
  { value: 'fees', label: 'Fees, licences & bank', hint: '' },
  { value: 'marketing', label: 'Marketing', hint: 'Cards, flyers, the website' },
  { value: 'other', label: 'Other', hint: '' }
];

export const EVERY_MONTHS = [
  { value: '', label: 'One-off' },
  { value: '1', label: 'Every month' },
  { value: '2', label: 'Every 2 months' },
  { value: '3', label: 'Every 3 months' },
  { value: '6', label: 'Every 6 months' },
  { value: '12', label: 'Every year' }
];

export const LIFE_BASES = [
  { value: 'hours', label: 'Hours of use', hint: 'Charged to jobs by the hour: (cost − salvage) ÷ life hours' },
  { value: 'years', label: 'Years', hint: 'Wears out whether it is used or not: the yearly depreciation goes into overheads' }
];

export const categoryLabel = (v) => EXPENSE_CATEGORIES.find((c) => c.value === v)?.label || v || 'Other';
export const everyLabel = (n) => (n ? (EVERY_MONTHS.find((e) => e.value === String(n))?.label || `Every ${n} months`).toLowerCase() : 'one-off');

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const round4 = (n) => Math.round((Number(n) + Number.EPSILON) * 10000) / 10000;

/** A recurring amount as a yearly figure; a one-off is itself. */
export function annualised(amount, everyMonths) {
  const n = Number(everyMonths) || 0;
  return n > 0 ? round2(((Number(amount) || 0) * 12) / n) : round2(amount || 0);
}

/** Hourly rate (hours basis) or yearly depreciation (years basis) for a piece of equipment. */
export function equipmentRate({ cost, salvage_value, life_basis, life_hours, life_years }) {
  const depreciable = Math.max(0, round2((Number(cost) || 0) - (Number(salvage_value) || 0)));
  const hours = Number(life_hours) || 0;
  const years = Number(life_years) || 0;
  return {
    depreciable,
    hourly_rate: life_basis !== 'years' && hours > 0 ? round4(depreciable / hours) : null,
    yearly_depreciation: life_basis === 'years' && years > 0 ? round2(depreciable / years) : null
  };
}

/** "3 Mar 2026" from 'YYYY-MM-DD' (Brisbane noon so the day never slips). */
export const fmtDate = (ymd) => (ymd ? new Date(`${ymd}T12:00:00+10:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/** "1,000" / "2.5" — a number with thousands separators and no trailing zeros. */
export const fmtNum = (n, dp = 2) => (Number(n) || 0).toLocaleString('en-AU', { maximumFractionDigits: dp });

// Reports + loans (GID-Session-016)
export const PERIODS = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_fy', label: 'This financial year' },
  { value: 'last_fy', label: 'Last financial year' },
  { value: 'this_year', label: 'This calendar year' },
  { value: 'last_12_months', label: 'Last 12 months' }
];

/** "Sept 2026" from 'YYYY-MM'. */
export const monthLabel = (ym) => (ym ? new Date(`${ym}-15T12:00:00+10:00`).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : '');

export const LOAN_STATUS = { open: 'Open', paid: 'Paid off', closed: 'Closed' };
